import { run } from 'shared'
import type { ReactNode } from 'react'

type INodeItemDirectoryIconProps = {
    color: string
    emoji?: ReactNode
    scale?: number
    mode?: 'display' | 'upload'
    onUploadClick?: () => void
}

export const NodeItemDirectoryIcon = ({
    color,
    emoji,
    scale = 1,
    mode = 'display',
    onUploadClick,
}: INodeItemDirectoryIconProps) => {
    // Helper to adjust brightness to 80%
    const adjustBrightness = (hexColor: string, factor: number) => {
        const hex = hexColor.replace('#', '')
        const r = parseInt(hex.substring(0, 2), 16)
        const g = parseInt(hex.substring(2, 4), 16)
        const b = parseInt(hex.substring(4, 6), 16)

        return `#${Math.round(r * factor)
            .toString(16)
            .padStart(2, '0')}${Math.round(g * factor)
            .toString(16)
            .padStart(2, '0')}${Math.round(b * factor)
            .toString(16)
            .padStart(2, '0')}`
    }

    const backColor = adjustBrightness(color, 0.8)
    const gradientId = `paint0_linear_${Math.random().toString(36).substr(2, 9)}`
    const gradientId2 = `paint1_linear_${Math.random().toString(36).substr(2, 9)}`
    const filterId = `filter1_i_${Math.random().toString(36).substr(2, 9)}`
    const clipPathId = `bgblur_${Math.random().toString(36).substr(2, 9)}_clip_path`

    return (
        <svg width={47 * scale} height={38 * scale} viewBox="0 0 47 38" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M0.5 3.68C0.5 1.64759 2.14759 0 4.18 0H14.4533C15.2496 0 16.0243 0.258254 16.6613 0.736L20.2187 3.404C20.8557 3.88174 21.6304 4.14 22.4267 4.14H42.82C44.8524 4.14 46.5 5.78759 46.5 7.82V24.38C46.5 26.4124 44.8524 28.06 42.82 28.06H4.18C2.14759 28.06 0.5 26.4124 0.5 24.38V3.68Z"
                fill={backColor}
            />
            <path
                d="M4.17969 0.230469H14.4531C15.1996 0.230469 15.9263 0.472036 16.5234 0.919922L20.0811 3.58789C20.7578 4.0954 21.5808 4.37012 22.4268 4.37012H42.8203C44.7255 4.37029 46.2695 5.91503 46.2695 7.82031V24.3799C46.2695 26.2852 44.7256 27.8299 42.8203 27.8301H4.17969C2.27445 27.8299 0.730469 26.2852 0.730469 24.3799V3.67969C0.730638 1.77455 2.27455 0.230638 4.17969 0.230469Z"
                stroke="white"
                strokeOpacity="0.1"
                strokeWidth="0.46"
            />
            <path
                data-figma-bg-blur-radius="1.932"
                d="M4.17969 9.43066H42.8203C44.7254 9.43083 46.2694 10.9747 46.2695 12.8799V33.5801C46.2695 35.4854 44.7256 37.0301 42.8203 37.0303H4.17969C2.27445 37.0301 0.730469 35.4854 0.730469 33.5801V12.8799C0.730638 10.9747 2.27455 9.43083 4.17969 9.43066Z"
                fill={color}
                stroke={`url(#${gradientId2})`}
                strokeWidth="0.46"
            />

            {run(() => {
                if (emoji) {
                    return (
                        <foreignObject x="0" y="0" width="47" height="46">
                            <div
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                }}
                            >
                                {emoji}
                            </div>
                        </foreignObject>
                    )
                }

                if (mode === 'display') {
                    return (
                        <g filter={`url(#${filterId})`}>
                            <path
                                d="M18.5231 24.7801L23.2695 17.3767C23.377 17.2091 23.6219 17.2091 23.7294 17.3767L28.4758 24.7801C28.5277 24.861 28.5332 24.9632 28.4904 25.0492L26.813 28.4204C26.6818 28.6841 26.3235 28.7273 26.1333 28.5024L21.4032 22.9071C21.2844 22.7667 21.0636 22.7813 20.9645 22.9363L20.4355 23.7633C20.3711 23.8639 20.3796 23.9947 20.4565 24.0862L22.3779 26.3721C22.4636 26.474 22.4633 26.623 22.377 26.7246L20.8657 28.5044C20.6753 28.7286 20.3176 28.6851 20.1866 28.4217L18.5085 25.0492C18.4657 24.9632 18.4712 24.861 18.5231 24.7801Z"
                                fill="white"
                                fillOpacity="0.8"
                            />
                        </g>
                    )
                }

                if (mode === 'upload') {
                    const uploadFilterId = `filter0_i_${Math.random().toString(36).substr(2, 9)}`
                    return (
                        <foreignObject x="15.1" y="14.35" width="17.8" height="18">
                            <div
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                                onClick={onUploadClick}
                            >
                                <svg
                                    width="14"
                                    height="14.5"
                                    viewBox="0 0 28 29"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{
                                        transition: 'transform 0.2s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'scale(1.1)'
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'scale(1)'
                                    }}
                                >
                                    <g filter={`url(#${uploadFilterId})`}>
                                        <circle cx="14" cy="14.5" r="14" fill="#FAFAFA" fillOpacity="0.2" />
                                    </g>
                                    <circle
                                        cx="14"
                                        cy="14.5"
                                        r="13.4167"
                                        stroke="white"
                                        strokeOpacity="0.61"
                                        strokeWidth="1.16667"
                                        strokeDasharray="3.5 3.5"
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M14 9.25C14.3222 9.25 14.5833 9.51117 14.5833 9.83333V13.9167H18.6667C18.9888 13.9167 19.25 14.1778 19.25 14.5C19.25 14.8222 18.9888 15.0833 18.6667 15.0833H14.5833V19.1667C14.5833 19.4888 14.3222 19.75 14 19.75C13.6778 19.75 13.4167 19.4888 13.4167 19.1667V15.0833H9.33333C9.01117 15.0833 8.75 14.8222 8.75 14.5C8.75 14.1778 9.01117 13.9167 9.33333 13.9167H13.4167V9.83333C13.4167 9.51117 13.6778 9.25 14 9.25Z"
                                        fill="white"
                                    />
                                    <defs>
                                        <filter
                                            id={uploadFilterId}
                                            x="0"
                                            y="0.5"
                                            width="28"
                                            height="28.5"
                                            filterUnits="userSpaceOnUse"
                                            colorInterpolationFilters="sRGB"
                                        >
                                            <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                            <feBlend
                                                mode="normal"
                                                in="SourceGraphic"
                                                in2="BackgroundImageFix"
                                                result="shape"
                                            />
                                            <feColorMatrix
                                                in="SourceAlpha"
                                                type="matrix"
                                                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                                result="hardAlpha"
                                            />
                                            <feOffset dy="0.5" />
                                            <feGaussianBlur stdDeviation="0.5" />
                                            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                                            <feColorMatrix
                                                type="matrix"
                                                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.13 0"
                                            />
                                            <feBlend mode="normal" in2="shape" result="effect1_innerShadow" />
                                        </filter>
                                    </defs>
                                </svg>
                            </div>
                        </foreignObject>
                    )
                }
            })}

            <defs>
                <clipPath id={clipPathId} transform="translate(1.432 -7.2682)">
                    <path d="M4.17969 9.43066H42.8203C44.7254 9.43083 46.2694 10.9747 46.2695 12.8799V33.5801C46.2695 35.4854 44.7256 37.0301 42.8203 37.0303H4.17969C2.27445 37.0301 0.730469 35.4854 0.730469 33.5801V12.8799C0.730638 10.9747 2.27455 9.43083 4.17969 9.43066Z" />
                </clipPath>
                <filter
                    id={filterId}
                    x="18.48"
                    y="17.251"
                    width="10.0391"
                    height="11.6279"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                    <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                    />
                    <feOffset dy="0.23" />
                    <feGaussianBlur stdDeviation="0.23" />
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0" />
                    <feBlend mode="normal" in2="shape" result="effect1_innerShadow" />
                </filter>
                <linearGradient
                    id={gradientId}
                    x1="29.2773"
                    y1="33.3962"
                    x2="29.2773"
                    y2="12.3351"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={color} />
                    <stop offset="1" stopColor={color} />
                </linearGradient>
                <linearGradient
                    id={gradientId2}
                    x1="24.65"
                    y1="9.2002"
                    x2="24.65"
                    y2="37.2602"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="white" stopOpacity="0.34" />
                    <stop offset="1" stopColor="white" stopOpacity="0" />
                </linearGradient>
            </defs>
        </svg>
    )
}
